import { Entsorger } from '../entsorger';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { StammdatenService } from '../../stammdaten.service';
import { MessageService } from '../../../../services/message-service/message.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { KieneBackendApiService } from '../../../../services/backend-api/kiene-backend-api.service';
import { API_BASE_URL_SERVICE } from '../../../../kiene-core.config';
import { AppService } from '../../../../services/app/app.service';
import { EntsorgerDetailsDialogComponent } from '../entsorger-details-dialog/entsorger-details-dialog.component';
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogInput, ConfirmDialogResult } from '../../../dialogs/confirm-dialog/confirm-dialog';

@Component({
    selector: 'kiene-entsorger-favoriten',
    templateUrl: './entsorger-uebersicht.component.html',
    styleUrls: ['./entsorger-uebersicht.component.scss'],
})
export class EntsorgerUebersichtComponent implements OnInit, OnDestroy {
    favoritenEntsorger: Entsorger[];
    sortField: 'name' | 'vvvo' | 'adresse' | 'email' = 'name';
    sortDirection: 'asc' | 'desc' = 'asc';
    entsorgerFehlt = this.stammdatenService.getEntsorgerFehlt();
    watchEntsorgerFehltSub: Subscription;
    entsorgerFavoritenSubscription: Subscription;
    @Input('showReturnButton') showReturnButton = true;

    constructor(
        private readonly stammdatenService: StammdatenService,
        private readonly api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly messageService: MessageService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly appService: AppService,
        private readonly dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.ladeFavoriten();

        this.watchEntsorgerFehltSub = this.stammdatenService.watchEntsorgerFehlt().subscribe(fehlt => {
            this.entsorgerFehlt = fehlt;
        });
    }

    ngOnDestroy(): void {
        this.watchEntsorgerFehltSub?.unsubscribe();
        this.entsorgerFavoritenSubscription?.unsubscribe();
    }

    ladeFavoriten(): void {
        this.entsorgerFavoritenSubscription?.unsubscribe();
        this.entsorgerFavoritenSubscription = this.stammdatenService.loadEntsorgerFavoriten().subscribe({
            next: data => {
                this.favoritenEntsorger = [];
                this.favoritenEntsorger = data.records;
            },
            error: (err: any) => {
                this.messageService.errorMessage('Fehler beim Laden aller Entsorger', err);
            },
        });
    }

    setSort(field: 'name' | 'vvvo' | 'adresse' | 'email'): void {
        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            return;
        }

        this.sortField = field;
        this.sortDirection = 'asc';
    }

    getSortedEntsorger(): Entsorger[] {
        const list = this.favoritenEntsorger ?? [];
        const direction = this.sortDirection === 'asc' ? 1 : -1;

        return [...list].sort((a: Entsorger, b: Entsorger) => {
            const aValue = this.getSortValue(a, this.sortField);
            const bValue = this.getSortValue(b, this.sortField);
            return aValue.localeCompare(bValue, 'de') * direction;
        });
    }

    getAdresse(entsorger: Entsorger): string {
        const strasse = (entsorger?.strasse ?? '').toString().trim();
        const plz = (entsorger?.plz ?? '').toString().trim();
        const ort = (entsorger?.ort ?? '').toString().trim();
        const secondLine = `${plz}${plz && ort ? ' ' : ''}${ort}`.trim();
        const value = `${strasse}${strasse && secondLine ? ', ' : ''}${secondLine}`.trim();
        return value || '-';
    }

    editiereEntsorger(entsorger: Entsorger): void {
        const dialogRef = this.dialog.open(EntsorgerDetailsDialogComponent, {
            minWidth: this.appService?.isMobileScreen ? '100vw' : '400px',
            width: this.appService?.isMobileScreen ? '100%' : '',
            data: { entsorger: entsorger },
        });
        dialogRef.afterClosed().subscribe({
            next: () => {
                this.ladeFavoriten();
            },
        });
    }

    entsorgerHinzufuegen() {
        if (!this.appService.isMobileScreen) {
            const dialogRef = this.dialog.open(EntsorgerDetailsDialogComponent, {
                minWidth: '560px',
                width: '62vw',
                maxWidth: '900px',
            });
            dialogRef.afterClosed().subscribe({
                next: () => {
                    this.ladeFavoriten();
                },
            });
        } else {
            this.router.navigate(['details'], { relativeTo: this.route }).then();
        }
    }

    sendMail(entsorger: Entsorger) {
        if (entsorger.email) {
            const htmlAnchorElement = document.createElement('a');
            htmlAnchorElement.style.display = 'none';
            htmlAnchorElement.href = 'mailto:' + entsorger.email;
            document.body.appendChild(htmlAnchorElement);
            htmlAnchorElement.click();
        }
    }

    entsorgerEntfernen(entsorger: Entsorger): void {
        const input = new ConfirmDialogInput();
        input.headline = 'Entsorger löschen';
        input.text = `Möchten Sie den Entsorger ${entsorger.name} wirklich löschen?`;
        input.okButton = 'Löschen';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    this.stammdatenService.deleteEntsorgerFavorit(entsorger).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Der Entsorger wurde erfolgreich gelöscht.');
                            this.ladeFavoriten();
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler beim Löschen des Entsorgers', err);
                        },
                    });
                }
            },
        });
    }

    private getSortValue(entsorger: Entsorger, field: 'name' | 'vvvo' | 'adresse' | 'email'): string {
        if (field === 'adresse') {
            return this.getAdresse(entsorger).toLowerCase();
        }
        return (entsorger?.[field] ?? '').toString().toLowerCase();
    }

    goBack(): void {
        window.history.back();
    }

    get isMobileScreen(): boolean {
        return this.appService.isMobileScreen;
    }
}
